import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";

import type { FilterSet } from "@skilitics/designer-ui-components";
import { useFeatureSwitch, useStickyState } from "@skilitics/nautilus-core-designer";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import {
  LandingPageSettingsContext,
  type FilterSetOptions,
  type LandingPageSettings,
} from "./LandingPageSettingsContext";
import type { Ordering, OrderingDirection } from "./types";

const ROOT = "landingPage";

export const LandingPageSettingsProvider = ({ children }: PropsWithChildren) => {
  const isSuperUser = useFeatureSwitch("super-user");

  const [devFetchAllUsersModules, setDevFetchAllUsersModules] = useState(false);

  const [view, setView] = useStickyState<LandingPageSettings["view"]>(`${ROOT}_view`, "grid");

  const [ordering, setOrdering] = useState<Ordering>("updatedOn");

  const [orderingDirection, setOrderingDirection] = useState<OrderingDirection>("ascending");

  const [filterSets, setFilterSets] = useState<LandingPageSettings["filterSets"]>([]);

  const hasFilterSet = useCallback(
    (id: FilterSet<InteractModuleListItem>["id"]) => !!filterSets.find((filterSet) => filterSet.id === id),
    [filterSets],
  );

  const getFilterSet = useCallback(
    (id: FilterSet<InteractModuleListItem>["id"]) => filterSets.find((filterSet) => filterSet.id === id),
    [filterSets],
  );

  const addFilterSet = useCallback(
    (filterSet: FilterSet<InteractModuleListItem>, { replace }: FilterSetOptions = { replace: true }) => {
      if (filterSets.find(({ id }) => id === filterSet.id) && !replace) return;

      const updatedFilterSets = filterSets.filter(({ id }) => id !== filterSet.id);

      setFilterSets([...updatedFilterSets, filterSet]);
    },
    [filterSets],
  );

  const removeFilterSet = useCallback(
    (id: FilterSet<InteractModuleListItem>["id"]) => {
      const updatedFilterSets = filterSets.filter((filterSet) => filterSet.id !== id);

      setFilterSets(updatedFilterSets);
    },
    [filterSets],
  );

  const value = useMemo(
    () => ({
      view,
      setView,
      ordering,
      setOrdering,
      orderingDirection,
      setOrderingDirection,
      filterSets,
      hasFilterSet,
      getFilterSet,
      addFilterSet,
      removeFilterSet,
      isSuperUser,
      devFetchAllUsersModules,
      setDevFetchAllUsersModules,
    }),
    [
      addFilterSet,
      filterSets,
      getFilterSet,
      hasFilterSet,
      ordering,
      orderingDirection,
      removeFilterSet,
      setView,
      view,
      isSuperUser,
      devFetchAllUsersModules,
      setDevFetchAllUsersModules,
    ],
  );

  return <LandingPageSettingsContext.Provider value={value}>{children}</LandingPageSettingsContext.Provider>;
};
